import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  ListTodo, 
  Clock, 
  Calendar,
  CheckCircle,
  Ship,
  FileText,
  MessageSquare,
  ExternalLink,
  Anchor,
  GripHorizontal,
  ChevronUp,
  ChevronDown,
  Lock,
  Unlock
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_URL } from "../config/api";


// Storage keys
const STORAGE_KEYS = {
  panelHeight: 'globalTasksPanel_height',
  splitPosition: 'globalTasksPanel_split',
  isLocked: 'globalTasksPanel_locked',
  isCollapsed: 'globalTasksPanel_collapsed'
};

// Task source icons and colors
const TASK_SOURCES = {
  quote_request: { icon: FileText, color: 'bg-blue-500', label: 'Quote' },
  support: { icon: MessageSquare, color: 'bg-purple-500', label: 'Support' },
  pf: { icon: Ship, color: 'bg-emerald-500', label: 'PF' },
  transport_order: { icon: Ship, color: 'bg-amber-500', label: 'TO' },
  general: { icon: ListTodo, color: 'bg-slate-500', label: 'Task' },
  inspection: { icon: Clock, color: 'bg-red-500', label: 'Inspection' },
};

export function GlobalTasksPanel() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [arrivals, setArrivals] = useState([]);
  
  // Load saved state from localStorage
  const getSavedHeight = () => {
    const saved = localStorage.getItem(STORAGE_KEYS.panelHeight);
    return saved ? parseInt(saved, 10) : 350; // Default to 350px for better initial view
  };
  
  const getSavedSplit = () => {
    const saved = localStorage.getItem(STORAGE_KEYS.splitPosition);
    return saved ? parseFloat(saved) : 65;
  };
  
  const getSavedLocked = () => {
    return localStorage.getItem(STORAGE_KEYS.isLocked) === 'true';
  };
  
  // Panel height state (total height for the bottom section)
  const [panelHeight, setPanelHeight] = useState(getSavedHeight);
  // Collapsed by default so tasks/ETA don't cover page action buttons
  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem(STORAGE_KEYS.isCollapsed) !== 'false'
  );
  const COLLAPSED_HEIGHT = 30;
  const effectiveHeight = isCollapsed ? COLLAPSED_HEIGHT : panelHeight;
  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      localStorage.setItem(STORAGE_KEYS.isCollapsed, (!prev).toString());
      return !prev;
    });
  };
  const [isDraggingPanel, setIsDraggingPanel] = useState(false);
  
  // Split position between Tasks and ETA (percentage)
  const [splitPosition, setSplitPosition] = useState(getSavedSplit);
  const [isDraggingSplit, setIsDraggingSplit] = useState(false);
  
  // Lock state
  const [isLocked, setIsLocked] = useState(getSavedLocked);

  // Save to localStorage when values change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.panelHeight, panelHeight.toString());
  }, [panelHeight]);
  
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.splitPosition, splitPosition.toString());
  }, [splitPosition]);
  
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.isLocked, isLocked.toString());
  }, [isLocked]);

  // Add padding to body when panel is visible to prevent content from being hidden.
  // Also expose the panel height as a CSS variable so any page using
  // `min-h-screen` (which ignores body padding) can shrink itself with
  // `min-h-[calc(100vh-var(--tasks-panel-height,0px))]`. This fixes the
  // chat input on /chat being hidden underneath the tasks panel.
  useEffect(() => {
    document.body.style.paddingBottom = `${effectiveHeight}px`;
    document.documentElement.style.setProperty('--tasks-panel-height', `${effectiveHeight}px`);
    return () => {
      document.body.style.paddingBottom = '0px';
      document.documentElement.style.setProperty('--tasks-panel-height', '0px');
    };
  }, [effectiveHeight]);

  // Handle panel height drag (top resize handle)
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingPanel || isLocked) return;
      
      const windowHeight = window.innerHeight;
      const newHeight = windowHeight - e.clientY;
      
      // Clamp between 200px and 95% of window height (allow near full-screen)
      setPanelHeight(Math.max(200, Math.min(windowHeight * 0.95, newHeight)));
    };

    const handleMouseUp = () => {
      setIsDraggingPanel(false);
    };

    if (isDraggingPanel && !isLocked) {
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDraggingPanel, isLocked]);

  // Handle split position drag (between Tasks and ETA)
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingSplit || isLocked) return;
      
      const container = document.getElementById('global-tasks-inner');
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const newPosition = ((e.clientY - rect.top) / rect.height) * 100;
      
      // Clamp between 30% and 85%
      setSplitPosition(Math.max(30, Math.min(85, newPosition)));
    };

    const handleMouseUp = () => {
      setIsDraggingSplit(false);
    };

    if (isDraggingSplit && !isLocked) {
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else if (!isDraggingPanel) {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingSplit, isLocked, isDraggingPanel]);

  // Fetch tasks from all sources
  const fetchTasks = useCallback(async () => {
    if (!token) return;
    
    try {
      const [globalRes, pfRes] = await Promise.all([
        axios.get(`${API_URL}/staff-tasks/my-tasks`, { 
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/position-folders/my-tasks`, { 
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
      ]);

      const globalTasks = Array.isArray(globalRes.data) ? globalRes.data : [];
      const pfTasksData = pfRes.data?.tasks || pfRes.data || [];
      const pfTasks = Array.isArray(pfTasksData) ? pfTasksData : [];

      const allTasks = [
        ...globalTasks.map(t => ({ ...t, source_type: 'global' })),
        ...pfTasks.map(t => ({ ...t, source_type: t.type || 'pf' })),
      ];

      const uniqueTasks = Array.from(new Map(allTasks.map(t => [t.id, t])).values());
      setTasks(uniqueTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch upcoming arrivals (ETA Koper - Next 10 Days)
  const fetchArrivals = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_URL}/position-folders/eta-koper-overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      // Transform the data to match the expected format
      const arrivals = data?.arrivals || [];
      setArrivals(arrivals.map(a => ({
        pf_number: a.pf_number,
        customer: a.customer || a.customer_name,
        vessel: a.vessel_name,
        eta: a.eta
      })));
    } catch (error) {
      // Silently fail
      console.log("ETA arrivals fetch failed");
    }
  }, [token]);

  const completeTask = useCallback(async (task) => {
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, completed: true } : t));
    try {
      if (task.pf_id) {
        await axios.patch(`${API_URL}/position-folders/${task.pf_id}/tasks/${task.id}`, { completed: true }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.put(`${API_URL}/staff-tasks/${task.id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      }
      setTimeout(() => setTasks(prev => prev.filter(t => t.id !== task.id)), 400);
    } catch (e) {
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, completed: false } : t));
      console.error('complete task failed', e);
    }
  }, [token]);

  useEffect(() => {
    fetchTasks();
    fetchArrivals();
    
    const interval = setInterval(() => {
      fetchTasks();
      fetchArrivals();
    }, 60000);
    
    return () => clearInterval(interval);
  }, [fetchTasks, fetchArrivals]);

  // Handle task click - navigate to source
  const handleTaskClick = (task) => {
    if (task.source === 'quote_request' && task.reference) {
      navigate(`/offers/pricing?quote=${task.reference}`);
    } else if (task.source === 'support' && task.reference) {
      navigate(`/support-queue?session=${task.reference}`);
    } else if (task.pf_number) {
      navigate(`/position-folders?search=${task.pf_number}`);
    } else if (task.transport_order_id) {
      navigate(`/transport-orders?highlight=${task.transport_order_id}`);
    }
  };

  // Toggle lock
  const toggleLock = () => {
    setIsLocked(!isLocked);
  };

  // Filter tasks for Today and Next 5 days
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const fiveDaysFromNow = new Date(today);
  fiveDaysFromNow.setDate(fiveDaysFromNow.getDate() + 5);
  
  const todayTasks = tasks.filter(t => {
    if (t.completed) return false;
    if (!t.due_date) return true;
    const d = new Date(t.due_date);
    d.setHours(0, 0, 0, 0);
    return d.getTime() === today.getTime();
  });

  const upcomingTasks = tasks.filter(t => {
    if (t.completed) return false;
    if (!t.due_date) return false;
    const d = new Date(t.due_date);
    d.setHours(0, 0, 0, 0);
    return d.getTime() > today.getTime() && d.getTime() <= fiveDaysFromNow.getTime();
  });

  const pendingCount = tasks.filter(t => !t.completed).length;

  // Don't render for customers
  if (user?.user_type === 'customer') return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-40 bg-slate-50 border-t border-slate-200 shadow-lg"
      style={{ height: `${effectiveHeight}px` }}
    >
      {/* ============ TOP RESIZE HANDLE (Move whole section up/down) ============ */}
      <div 
        className={`h-6 flex items-center justify-center flex-shrink-0 select-none gap-2 bg-slate-50 ${isLocked ? 'cursor-default' : 'cursor-row-resize'}`}
      >
        {/* Lock Button - Left */}
        <button
          onClick={toggleLock}
          className={`px-2 py-1 rounded-lg transition-colors flex items-center gap-1 text-xs font-medium ${isLocked ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
          title={isLocked ? 'Unlock panel (allow resize)' : 'Lock panel position'}
        >
          {isLocked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
          {isLocked ? 'Locked' : 'Lock'}
        </button>
        
        {/* Drag Handle - Center */}
        <div 
          className={`h-2 flex-1 max-w-md flex items-center justify-center ${isDraggingPanel ? 'bg-amber-400' : isLocked ? 'bg-slate-100' : 'bg-slate-200 hover:bg-slate-300'} rounded-full transition-colors`}
          onMouseDown={(e) => {
            if (isLocked) return;
            e.preventDefault();
            setIsDraggingPanel(true);
          }}
        >
          <GripHorizontal className={`h-4 w-4 ${isLocked ? 'text-slate-300' : 'text-slate-400'}`} />
        </div>
        
        {/* Collapse/Expand toggle - Right */}
        <button
          onClick={toggleCollapsed}
          className="w-16 px-2 py-1 rounded-lg transition-colors flex items-center justify-center gap-1 text-xs font-medium bg-slate-200 text-slate-600 hover:bg-slate-300"
          title={isCollapsed ? 'Show tasks & arrivals' : 'Hide tasks & arrivals'}
          data-testid="tasks-panel-collapse-toggle"
        >
          {isCollapsed ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {isCollapsed ? 'Tasks' : 'Hide'}
        </button>
      </div>

      {/* ============ CONTENT WRAPPER (matches content width) ============ */}
      <div 
        id="global-tasks-inner"
        className={`flex-1 flex flex-col w-full px-6 overflow-hidden h-[calc(100%-24px)] ${isCollapsed ? 'hidden' : ''}`}
      >
        {/* ============ TASKS & ACTIVITIES PANEL ============ */}
        <div 
          className="bg-white rounded-xl border shadow-sm overflow-hidden flex-shrink-0 flex flex-col"
          style={{ height: `${splitPosition}%`, minHeight: '100px' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 text-white flex items-center justify-between rounded-t-xl flex-shrink-0">
            <h3 className="font-semibold flex items-center gap-2">
              <ListTodo className="h-5 w-5" />
              Tasks & Activities
            </h3>
            <div className="flex items-center gap-2">
              {isLocked && (
                <span className="text-xs bg-white/30 px-2 py-0.5 rounded flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  Locked
                </span>
              )}
              <span className="text-xs bg-white/20 px-2 py-1 rounded">
                {pendingCount} pending
              </span>
            </div>
          </div>
          
          {/* Split Content - Today | Next 5 Days */}
          <div className="flex flex-1 overflow-hidden">
            {/* TODAY Column */}
            <div className="flex-1 border-r overflow-hidden flex flex-col">
              <div className="bg-red-50 px-3 py-2 border-b flex items-center justify-between flex-shrink-0">
                <h4 className="font-semibold text-red-700 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Today
                </h4>
                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                  {todayTasks.length}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto">
                {todayTasks.length === 0 ? (
                  <div className="p-4 text-center text-slate-400 text-sm">
                    <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-300" />
                    All caught up for today!
                  </div>
                ) : (
                  todayTasks.map((task) => (
                    <TaskItem 
                      key={task.id} 
                      task={task} 
                      onClick={() => handleTaskClick(task)}
                      onComplete={completeTask}
                    />
                  ))
                )}
              </div>
            </div>

            {/* NEXT 5 DAYS Column */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="bg-blue-50 px-3 py-2 border-b flex items-center justify-between flex-shrink-0">
                <h4 className="font-semibold text-blue-700 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Next 5 Working Days
                </h4>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                  {upcomingTasks.length}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto">
                {upcomingTasks.length === 0 ? (
                  <div className="p-4 text-center text-slate-400 text-sm">
                    <Calendar className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    No upcoming tasks
                  </div>
                ) : (
                  upcomingTasks.map((task) => (
                    <TaskItem 
                      key={task.id} 
                      task={task} 
                      onClick={() => handleTaskClick(task)}
                      onComplete={completeTask}
                      showDate
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ============ SPLIT RESIZE HANDLE (between Tasks and ETA) ============ */}
        <div 
          className={`h-3 flex items-center justify-center flex-shrink-0 select-none my-1 ${isLocked ? 'cursor-default' : 'cursor-row-resize'}`}
          onMouseDown={(e) => {
            if (isLocked) return;
            e.preventDefault();
            setIsDraggingSplit(true);
          }}
        >
          <div className={`h-2 w-full ${isDraggingSplit ? 'bg-cyan-400' : isLocked ? 'bg-slate-100' : 'bg-slate-200 hover:bg-slate-300'} rounded-full flex items-center justify-center transition-colors`}>
            <div className={`w-10 h-1 ${isLocked ? 'bg-slate-200' : 'bg-slate-400'} rounded-full`}></div>
          </div>
        </div>

        {/* ============ ETA KOPER PANEL ============ */}
        <div 
          className="bg-white rounded-xl border shadow-sm overflow-hidden flex-1 flex flex-col"
          style={{ minHeight: '80px' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 px-4 py-2 text-white flex items-center justify-between rounded-t-xl flex-shrink-0">
            <h3 className="font-semibold flex items-center gap-2">
              <Anchor className="h-5 w-5" />
              ETA Koper - Next 10 Days
            </h3>
            <span className="text-xs bg-white/20 px-2 py-1 rounded">
              {arrivals.length} arrivals
            </span>
          </div>
          
          {/* Arrivals List */}
          <div className="flex-1 overflow-y-auto">
            {arrivals.length === 0 ? (
              <div className="p-4 text-center text-slate-400 text-sm">
                <Ship className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                No arrivals expected
              </div>
            ) : (
              <div className="divide-y">
                {arrivals.map((arrival, idx) => (
                  <div 
                    key={idx}
                    className="p-2 hover:bg-blue-50 cursor-pointer transition-colors flex items-center justify-between"
                    onClick={() => navigate(`/position-folders?search=${arrival.pf_number}`)}
                  >
                    <div>
                      <span className="font-medium text-sm text-slate-900">{arrival.pf_number}</span>
                      <p className="text-xs text-slate-500">{arrival.vessel} • {arrival.customer}</p>
                    </div>
                    <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      {arrival.eta}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Individual Task Item Component
function TaskItem({ task, onClick, onComplete, showDate = false }) {
  const sourceConfig = TASK_SOURCES[task.source] || TASK_SOURCES.general;
  const SourceIcon = sourceConfig.icon;

  return (
    <div 
      className="p-2 border-b hover:bg-amber-50 flex items-start gap-2 cursor-pointer transition-colors group"
      onClick={onClick}
    >
      <input
        type="checkbox"
        checked={task.completed || false}
        onChange={(e) => { e.stopPropagation(); if (e.target.checked && onComplete) onComplete(task); }}
        onClick={(e) => e.stopPropagation()}
        className="h-4 w-4 mt-0.5 rounded border-slate-300 cursor-pointer"
        data-testid={`task-complete-${task.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <span className={`inline-flex items-center justify-center w-4 h-4 rounded ${sourceConfig.color} text-white`}>
            <SourceIcon className="h-2.5 w-2.5" />
          </span>
          <p className={`text-sm font-medium truncate ${task.completed ? 'line-through text-slate-400' : 'text-slate-900'}`}>
            {task.title || task.description}
          </p>
        </div>
        <p className="text-xs text-slate-500 truncate">
          {task.reference || task.pf_number || task.quote_number || ''}
          {task.customer && ` • ${task.customer}`}
        </p>
        {showDate && task.due_date && (
          <p className="text-xs text-blue-600">
            {new Date(task.due_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
          </p>
        )}
      </div>
      {task.priority === 'high' && (
        <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded">!</span>
      )}
      <ExternalLink className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

export default GlobalTasksPanel;
